// ─────────────────────────────────────────────────────────────────────────────
// The headless half: a queue of uploads with progress, cancel and retry.
//
// Split from the component on purpose. A page that wants its own presentation
// (an avatar cropper, a bulk importer) can take this hook and render whatever
// it likes; `FileUpload` is one presentation of it.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from 'react';
import { media } from '@repletoryreach/sdk-api';
import { isApiError } from '@repletoryreach/sdk-api/errors';
import { DEFAULT_LABELS, toMediaValue, type FileUploadLabels, type MediaValue } from './types.js';

export type UploadStatus = 'uploading' | 'done' | 'error' | 'cancelled';

export interface UploadItem {
  /** Stable across a retry, so React keys do not churn. */
  key: string;
  file: File;
  status: UploadStatus;
  /** 0-100. */
  percent: number;
  /** Set when `status` is `error`: already translated, ready to render. */
  error?: string;
  /** Set when `status` is `done`. */
  value?: MediaValue;
}

export interface UseMediaUploadOptions {
  /** Called for each file that finishes, in completion order. */
  onUploaded?: (value: MediaValue, file: File) => void;
  onError?: (message: string, file: File) => void;
  /** Strings for the error messages this hook produces. */
  labels?: Partial<FileUploadLabels>;
  /** Client-side ceiling in bytes. The service enforces its own as well. */
  maxSize?: number;
}

export interface UseMediaUpload {
  items: UploadItem[];
  /** Queue files. Returns immediately; watch `items` for progress. */
  add: (files: File[]) => void;
  cancel: (key: string) => void;
  retry: (key: string) => void;
  /** Drop one finished row from the list. Does not touch the stored file. */
  dismiss: (key: string) => void;
  clear: () => void;
  /** True while any file is still going out. */
  busy: boolean;
}

let seq = 0;

export function useMediaUpload(opts: UseMediaUploadOptions = {}): UseMediaUpload {
  const [items, setItems] = useState<UploadItem[]>([]);
  const aborts = useRef(new Map<string, AbortController>());

  /*
   * The callbacks are read through a ref so that starting an upload does not
   * capture the render in which it began. Without this, a parent that passes an
   * inline `onUploaded` would either stale-close over old state or force every
   * in-flight upload to be re-created on each render.
   */
  const latest = useRef(opts);
  latest.current = opts;

  // Cancel everything still in flight when the form unmounts, so a closed
  // drawer does not keep pushing bytes at the service.
  useEffect(() => {
    const map = aborts.current;
    return () => { for (const c of map.values()) c.abort(); map.clear(); };
  }, []);

  const patch = useCallback((key: string, next: Partial<UploadItem>) => {
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...next } : it)));
  }, []);

  const start = useCallback((key: string, file: File) => {
    const controller = new AbortController();
    aborts.current.set(key, controller);
    patch(key, { status: 'uploading', percent: 0, error: undefined });

    media
      .upload(file, {
        signal: controller.signal,
        // A 64MB ceiling over a slow uplink outlasts the default client
        // timeout, and the request is cancellable, so a long one is safe.
        timeoutMs: 10 * 60 * 1000,
        onProgress: (percent) => patch(key, { percent })
      })
      .then((res) => {
        aborts.current.delete(key);
        const value = toMediaValue(res.data);
        patch(key, { status: 'done', percent: 100, value });
        latest.current.onUploaded?.(value, file);
      })
      .catch((e: unknown) => {
        aborts.current.delete(key);
        if (isAbort(e)) { patch(key, { status: 'cancelled' }); return; }
        const message = describe(e, latest.current.labels);
        patch(key, { status: 'error', error: message });
        latest.current.onError?.(message, file);
      });
  }, [patch]);

  const add = useCallback((files: File[]) => {
    const max = latest.current.maxSize;
    const queued: UploadItem[] = [];
    const rejected: UploadItem[] = [];
    for (const file of files) {
      const key = `u${++seq}`;
      if (max && file.size > max) {
        rejected.push({
          key, file, status: 'error', percent: 0,
          error: label(latest.current.labels, 'errorTooLarge')
        });
      } else {
        queued.push({ key, file, status: 'uploading', percent: 0 });
      }
    }
    if (queued.length || rejected.length) setItems((prev) => [...prev, ...queued, ...rejected]);
    for (const it of queued) start(it.key, it.file);
    for (const it of rejected) latest.current.onError?.(it.error!, it.file);
  }, [start]);

  const cancel = useCallback((key: string) => {
    aborts.current.get(key)?.abort();
    aborts.current.delete(key);
    patch(key, { status: 'cancelled' });
  }, [patch]);

  const retry = useCallback((key: string) => {
    setItems((prev) => {
      const item = prev.find((it) => it.key === key);
      if (item) queueMicrotask(() => start(key, item.file));
      return prev;
    });
  }, [start]);

  const dismiss = useCallback((key: string) => {
    aborts.current.get(key)?.abort();
    aborts.current.delete(key);
    setItems((prev) => prev.filter((it) => it.key !== key));
  }, []);

  const clear = useCallback(() => {
    for (const c of aborts.current.values()) c.abort();
    aborts.current.clear();
    setItems([]);
  }, []);

  return { items, add, cancel, retry, dismiss, clear, busy: items.some((i) => i.status === 'uploading') };
}

function isAbort(e: unknown): boolean {
  return e instanceof DOMException && e.name === 'AbortError';
}

function label(labels: Partial<FileUploadLabels> | undefined, key: keyof FileUploadLabels): string {
  return labels?.[key] ?? DEFAULT_LABELS[key];
}

/**
 * Turn a failure into a sentence worth showing.
 *
 * The upload endpoint's failures are mostly things the person can act on, and
 * "Request failed with status 422" tells them none of it: a virus hit means
 * "this file is not going to work", a 503 from the scanner means "try again in
 * a minute", and `not_configured` is not their problem at all.
 */
export function describe(e: unknown, labels?: Partial<FileUploadLabels>): string {
  if (isApiError(e)) {
    // A 413 from a proxy in front of the media service carries no body of ours, and used to show as
    // "POST /v1/media → 413" (18 October 2026). Whoever refused it, the file was too large.
    if (e.status === 413) return label(labels, 'errorTooLarge');
    switch (e.code) {
      case 'INFECTED': {
        const sig = (e.body as { signature?: string } | null)?.signature;
        const base = label(labels, 'errorInfected');
        return sig ? `${base} (${sig})` : base;
      }
      case 'TOO_LARGE': return label(labels, 'errorTooLarge');
      case 'SCAN_UNAVAILABLE':
      case 'SCAN_NOT_CONFIGURED': return label(labels, 'errorScanUnavailable');
      case 'not_configured': return label(labels, 'errorNotConfigured');
      default: return e.message || label(labels, 'errorGeneric');
    }
  }
  return (e as Error)?.message || label(labels, 'errorGeneric');
}
